import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { Header } from "@/components/layout/Header";
import { BlogBreadcrumb } from "@/components/seo/BlogBreadcrumb";
import { Footer } from "@/components/layout/Footer";
import { SEOContentBlock } from "@/components/seo/SEOContentBlock";
import { RelatedReads } from "@/components/seo/RelatedReads";
import { getRelatedPosts } from "@/content/blogPosts";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CalendarDays } from "lucide-react";
import heroImage from "@/assets/tubing/family-tubing-fun.jpg";
import cabanaImg from "@/assets/cabanas/family-group-cabana.webp";
import riverImg from "@/assets/gallery/clear-river.jpg";

const PUBLISH_DATE = "May 9, 2026";
const CANONICAL = "/blog/schlitterbahn-vs-tubing-guadalupe";
const TITLE = "Schlitterbahn vs. Tubing the Guadalupe — Which Is Better? | Son's";
const DESCRIPTION =
  "Schlitterbahn vs. tubing the Guadalupe River — which is better for families near New Braunfels? Cost, crowds, kid-friendliness, and the case for both.";

const FAQS = [
  {
    q: "Is tubing the Guadalupe cheaper than Schlitterbahn?",
    a: "Yes. A day pass to Schlitterbahn for a family of four runs several hundred dollars, while a Son's Guadalupe cabana rental on the Guadalupe River starts around $69 and includes parking, a picnic table, and direct river access for up to 6 guests. Add tube rentals and you're still well under park-ticket pricing.",
  },
  {
    q: "Which is better for young kids?",
    a: "Schlitterbahn has shallow kiddie areas, but the Guadalupe River — especially the calm stretches we float — is gentler, less crowded, and lets kids splash and swim freely with a life jacket. Many families do both: river one day, water park the next.",
  },
  {
    q: "Do I need to choose between them?",
    a: "Not at all. Many of our guests stay overnight in a glamping tent or tent camping site on River Road and split their trip — one day tubing the Guadalupe, one day at Schlitterbahn. New Braunfels is small enough that everything is 15–20 minutes apart.",
  },
];

const schemaJson = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://sonsguadalupe.com/" },
        { "@type": "ListItem", position: 2, name: "Blog", item: "https://sonsguadalupe.com/blog" },
        { "@type": "ListItem", position: 3, name: "Schlitterbahn vs. Tubing the Guadalupe — Which Is Better for Families?", item: `https://sonsguadalupe.com/blog/schlitterbahn-vs-tubing-guadalupe` },
      ],
    },
    {
      "@type": "BlogPosting",
      headline: "Schlitterbahn vs. Tubing the Guadalupe — Which Is Better for Families?",
      description: DESCRIPTION,
      image: "https://sonsguadalupe.com/og-image.png",
      author: { "@type": "Organization", name: "Son's Guadalupe" },
      datePublished: "2026-05-09",
      mainEntityOfPage: { "@type": "WebPage", "@id": `https://sonsguadalupe.com${CANONICAL}` },
      keywords:
        "schlitterbahn alternative, schlitterbahn vs guadalupe river, things to do besides schlitterbahn, tubing new braunfels, family things to do new braunfels",
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQS.map(({ q, a }) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    },
  ],
};

const BlogSchlitterbahnVsTubing = () => {
  const related = getRelatedPosts("schlitterbahn-vs-tubing-guadalupe", 3);
  return (
    <div className="min-h-screen">
      <SEOHead title={TITLE} description={DESCRIPTION} canonical={CANONICAL} schemaJson={schemaJson} />
      <Header />
      <BlogBreadcrumb />
      <main className="pt-24 pb-16">
        <div className="relative h-[55vh] min-h-[400px]">
          <img fetchPriority="high" loading="eager" decoding="async" src={heroImage} alt="Family tubing the Guadalupe River near New Braunfels — Schlitterbahn alternative" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
            <div className="container-narrow mx-auto">
              <div className="flex items-center gap-2 text-white/80 text-sm mb-3">
                <CalendarDays className="w-4 h-4" /> {PUBLISH_DATE}
              </div>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-white max-w-4xl leading-tight">
                Schlitterbahn vs. Tubing the Guadalupe — Which Is Better for Families?
              </h1>
            </div>
          </div>
        </div>

        <article className="section-padding bg-background">
          <div className="container-narrow mx-auto max-w-3xl px-4">
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-primary hover:underline mb-8">
              <ArrowLeft className="w-4 h-4" /> Back to Blog
            </Link>

            <div className="prose prose-lg max-w-none text-foreground space-y-6">
              <p className="text-xl leading-relaxed">
                Planning a New Braunfels trip and torn between <strong>Schlitterbahn</strong> and <strong>tubing the Guadalupe River</strong>? Both are great. But they're very different vacations. Here's an honest, head-to-head look so you can pick the right one — or do both.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">Cost</h2>
              <p>
                Schlitterbahn day passes for a family of four run several hundred dollars. A Son's Guadalupe cabana rental on the Guadalupe River starts around $69 and includes parking, picnic table, and river access for 6. Tube rentals add a small per-person fee. Tubing wins on cost — by a lot.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">Crowds & Vibe</h2>
              <p>
                Schlitterbahn is loud, busy, and full-tilt fun — peak Texas summer energy. The Guadalupe River is quieter, slower, more nature-forward. You float past cypress trees, splash in clear water, and eat lunch at your own picnic table. Different kinds of fun.
              </p>

              <img src={riverImg} alt="Clear, calm stretch of the Guadalupe River near New Braunfels" loading="lazy" className="w-full rounded-2xl shadow-elevated my-8" />

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">Kid-Friendliness</h2>
              <p>
                Schlitterbahn has dedicated kiddie areas. The Guadalupe wins on calm, swim-friendly stretches with no lines and parents who can actually relax. With life jackets, the river is excellent for ages 5+ — and toddlers love splashing near the cabana.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">Logistics</h2>
              <p>
                Schlitterbahn = parking lot + locker + crowds. Cabana on the Guadalupe = your own private basecamp with parking, shade, table, river. If you've ever managed a cooler and three kids in a Schlitterbahn parking lot, you know.
              </p>

              <img src={cabanaImg} alt="Family relaxing at a cabana on the Guadalupe River near New Braunfels" loading="lazy" className="w-full rounded-2xl shadow-elevated my-8" />

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">The Best Move? Do Both.</h2>
              <p>
                New Braunfels is small. Stay overnight in a Son's glamping tent or tent camping site on River Road. Day one: tube the Guadalupe and chill at the cabana. Day two: hit Schlitterbahn. Best of both worlds, one trip.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">FAQs</h2>
              <div className="space-y-6">
                {FAQS.map((f) => (
                  <div key={f.q}>
                    <h3 className="font-heading text-lg font-semibold text-foreground">{f.q}</h3>
                    <p className="mt-2">{f.a}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
                <Link to="/book-now"><Button variant="default" size="lg" className="text-lg px-10 py-6">Book a Cabana</Button></Link>
                <Link to="/tubing"><Button variant="outline" size="lg" className="text-lg px-10 py-6">See Tubing Info</Button></Link>
              </div>
            </div>
          </div>
        </article>
        <RelatedReads posts={related} />

      </main>
      <SEOContentBlock
        eyebrow="More New Braunfels Resources"
        heading="The Calmer, Cheaper Alternative to a Theme Park Day"
        paragraphs={[
          "Tubing the Guadalupe River from a private cabana on River Road costs less than Schlitterbahn, has zero lines, and gives families room to actually breathe. Pair it with glamping near New Braunfels for a full weekend.",
        ]}
        links={[
          { label: "Cabanas", to: "/cabanas" },
          { label: "Tubing", to: "/tubing" },
          { label: "Glamping", to: "/glamping" },
          { label: "Family Getaways", to: "/family-getaways-new-braunfels" },
        ]}
      />
      <Footer />
    </div>
  );
};

export default BlogSchlitterbahnVsTubing;
